import type {
  ClientLibraryDiscoveryPanelItem,
  ClientLibraryId,
} from 'types/client-library'
import type { Thunk } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE,
  CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_LOAD,
  CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_LOAD_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_LOAD_SUCCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_SUCCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD,
  CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_NEXT,
  CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_NEXT_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_NEXT_SUCCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_SUCCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_RECENT_LOAD,
  CLIENT_LIBRARY_DISCOVERY_PANEL_RECENT_LOAD_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_RECENT_LOAD_SUCCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_REQUEST_ACCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_REQUEST_ACCESS_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_REQUEST_ACCESS_SUCCESS,
  CLIENT_LIBRARY_DISCOVERY_PANEL_SET_LOAD,
  CLIENT_LIBRARY_DISCOVERY_PANEL_SET_LOAD_FAIL,
  CLIENT_LIBRARY_DISCOVERY_PANEL_SET_LOAD_SUCCESS,
  CLIENT_LIBRARY_SUGGESTED_LIBRARIES_LOAD,
  CLIENT_LIBRARY_SUGGESTED_LIBRARIES_LOAD_FAIL,
  CLIENT_LIBRARY_SUGGESTED_LIBRARIES_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads client library discovery panel with optional search and filtering.
 * @param query - Search query to filter libraries or null for all
 * @param filter - Filter criteria to apply or null for no filter
 * @returns Thunk that loads discovery panel and tracks analytics
 */
export function clientLibraryDiscoveryPanelLoad(
  query: string | null | undefined,
  filter: string | null | undefined,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD,
    })

    try {
      if (query) {
        dispatch(
          sendMixpanelEvent(
            'Discover Music - Supervisor - search in Browse Catalogs',
          ),
        )
      }

      const response = await api.getClientLibraryDiscoveryPanel(query, filter)

      if (query) {
        const resultsCount = response?.count || 0
        const data = {
          query,
          filter,
          results: resultsCount,
        }
        let message = `Discover Music - Supervisor - Successful search`

        if (resultsCount == 0) {
          message = 'Discover Music - Supervisor - Unsuccessful search'
        }

        dispatch(sendMixpanelEvent(message, data))
      }

      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_SUCCESS,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads the next page of discovery panel results using pagination.
 * @param url - Pagination URL for the next page of discovery results
 * @returns Thunk that loads next page of discovery panel results
 */
export function clientLibraryDiscoveryPanelLoadNext(url: string): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_NEXT,
    })

    try {
      const response = await api.getUrl<any>(
        url,
        null,
        'api.getClientLibraryDiscoveryPanelLoadNext',
      )
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_NEXT_SUCCESS,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_LOAD_NEXT_FAIL,
        error,
      })
    }
  }
}
/**
 * Toggles favorite status for a client library in the discovery panel.
 * @param libraryId - ID of the client library to toggle favorite status for
 * @param isFavorite - Whether to mark the library as favorite or not
 * @returns Thunk that updates favorite status and shows success/error notifications
 */
export function clientLibraryDiscoveryPanelFavorite(
  libraryId: ClientLibraryId,
  isFavorite: boolean,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE,
      libraryId,
      isFavorite,
    })

    try {
      await api.clientLibraryDiscoveryPanelFavorite(libraryId, isFavorite)
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_SUCCESS,
      })
      pushUINotification({
        message: isFavorite
          ? 'Added to Favorite Catalogs'
          : 'Removed from Favorite Catalogs',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_FAIL,
        libraryId,
        isFavorite,
      })
      pushUINotification({
        message: String(error),
        level: 'error',
      })
    }
  }
}
/**
 * Requests access to a client library from the discovery panel.
 * @param libraryId - ID of the client library to request access to
 * @param status - Current status of the library access
 * @returns Thunk that sends access request and shows success/error notifications
 */
export function clientLibraryDiscoveryPanelRequestAccess(
  libraryId: ClientLibraryId,
  status: ClientLibraryDiscoveryPanelItem['status'],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_REQUEST_ACCESS,
      libraryId,
    })

    try {
      await api.clientLibraryDiscoveryPanelRequestAccess(libraryId)
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_REQUEST_ACCESS_SUCCESS,
      })
      pushUINotification({
        message: 'Access request sent!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_REQUEST_ACCESS_FAIL,
        libraryId,
        status,
      })
      pushUINotification({
        message: String(error),
        level: 'error',
      })
    }
  }
}
/**
 * Loads the discovery panel set configuration.
 * @returns Thunk that loads discovery panel set data
 */
export function clientLibraryDiscoveryPanelSetLoad(): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_SET_LOAD,
    })

    try {
      const response = await api.getClientLibraryDiscoveryPanelSet()
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_SET_LOAD_SUCCESS,
        set: response.results,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_SET_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads suggested client libraries sorted by favorite status.
 * @returns Thunk that loads and sorts suggested libraries
 */
export function clientLibrarySuggestedLibrariesLoad(): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_SUGGESTED_LIBRARIES_LOAD,
    })

    try {
      const suggested: {
        count: number
        next: string | null | undefined
        previous: string | null | undefined
        results: ClientLibraryDiscoveryPanelItem[]
      } = await api.getClientLibraryDiscoveryPanel('', 'suggested', 1000)
      const libraries = suggested.results.sort(
        (a, b) => Number(a.is_favorite) - Number(b.is_favorite),
      )
      dispatch({
        type: CLIENT_LIBRARY_SUGGESTED_LIBRARIES_LOAD_SUCCESS,
        results: libraries,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_SUGGESTED_LIBRARIES_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads favorite client libraries from the discovery panel.
 * @returns Thunk that loads all favorite libraries
 */
export function clientLibraryDiscoveryPanelFavoriteLoad(): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_LOAD,
    })

    try {
      const libraries = await api.getClientLibraryDiscoveryPanel(
        '',
        'is_favorite',
        1000,
      )
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_LOAD_SUCCESS,
        response: libraries,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_FAVORITE_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads recently accessed client libraries from the discovery panel.
 * @returns Thunk that loads recent libraries (limited to 15 results)
 */
export function clientLibraryDiscoveryPaneRecentLoad(): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_DISCOVERY_PANEL_RECENT_LOAD,
    })

    try {
      const libraries = await api.getClientLibraryDiscoveryPanel(
        '',
        '',
        15,
        'recent',
      )
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_RECENT_LOAD_SUCCESS,
        response: libraries,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_DISCOVERY_PANEL_RECENT_LOAD_FAIL,
        error,
      })
    }
  }
}
